import { createMemo, For, Show, type JSX } from "solid-js";

import clsx from "~/util/html/style";

import { ArchArt, archLabelsInView } from "../art/arch_art";
import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import type { YourTeethSlide as YourTeethSlideData } from "../types";

/**
 * The mouth chart: planned teeth lit and labeled, and area work (a quadrant or arch) named, never counted as teeth;
 * framed, focused or shown after treatment as staff or Ask Twelve set its drawing.
 * A phone shows the arch without its side columns, which would shrink it past reading, and lists the labels under it.
 */
export function YourTeethSlide(props: { slide: YourTeethSlideData }): JSX.Element {
  const words = useDeckWords();
  // Only the teeth in the phone drawing's view, as the wide drawing calls out.
  const labels = createMemo(() =>
    archLabelsInView(props.slide.arch, props.slide.zoom, props.slide.drawing),
  );
  return (
    <>
      <div>
        <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
        <Show when={props.slide.areas.length > 0}>
          <ul class={styles["areas"]}>
            <For each={props.slide.areas}>{(area) => <li class={styles["chip"]}>{area}</li>}</For>
          </ul>
        </Show>
      </div>
      <div class={styles["m-art"]}>
        <div class={styles["m-wide"]}>
          <ArchArt
            arch={props.slide.arch}
            mode="plain"
            numbers
            title={words().t("teethInYourPlan")}
            zoom={props.slide.zoom}
            drawing={props.slide.drawing}
            after={props.slide.after}
          />
        </div>
        <div class={styles["m-phone"]}>
          <ArchArt
            arch={{ ...props.slide.arch, labels: [] }}
            mode="plain"
            numbers
            title={words().t("teethInYourPlan")}
            zoom={props.slide.zoom}
            drawing={props.slide.drawing}
            after={props.slide.after}
          />
          <Show when={labels().length > 0}>
            <ul class={styles["m-labels"]}>
              <For each={labels()}>
                {(label) => (
                  <li class={clsx(styles["chip"], styles["chip-t"])}>
                    #{label.tooth} {label.text}
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </div>
    </>
  );
}
